import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { configureGoogleAnalytics, trackEvent, trackPageView } from '@/lib/analytics'
import { t, type Locale } from '@/lib/i18n'

type ConsentPreferences = {
  analytics: boolean
  marketing: boolean
  preferences: boolean
}

type ConsentRecord = {
  version: 1
  updatedAt: string
  locale: Locale
  preferences: ConsentPreferences
}

const CONSENT_COOKIE = 'rubyinsights_consent'
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180
const DEFAULT_PREFERENCES: ConsentPreferences = {
  analytics: false,
  marketing: false,
  preferences: false,
}

function currentLocale(): Locale {
  return window.location.pathname.startsWith('/pt-br') ? 'pt-br' : 'en'
}

function readConsent(): ConsentRecord | null {
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${CONSENT_COOKIE}=`))
  if (!cookie) return null

  try {
    const record = JSON.parse(decodeURIComponent(cookie.slice(CONSENT_COOKIE.length + 1))) as ConsentRecord
    if (
      record.version !== 1 ||
      typeof record.preferences?.analytics !== 'boolean' ||
      typeof record.preferences?.marketing !== 'boolean' ||
      typeof record.preferences?.preferences !== 'boolean'
    ) {
      console.warn('Ignoring invalid cookie consent preferences.')
      return null
    }
    return record
  } catch (error) {
    console.warn('Could not read cookie consent preferences.', error)
    return null
  }
}

function writeConsent(record: ConsentRecord) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(record))}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`
  if (!document.cookie.split('; ').some((entry) => entry.startsWith(`${CONSENT_COOKIE}=`))) {
    throw new Error('Cookie consent could not be saved in this browser.')
  }
}

function measurementId() {
  return document.querySelector<HTMLMetaElement>('meta[name="google-analytics-id"]')?.content.trim() ?? ''
}

function PreferenceToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string
  description: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-xl border p-4">
      <span>
        <span className="block text-sm font-semibold text-ruby-dark">{label}</span>
        <span className="mt-1 block text-sm text-mid-blue-gray">{description}</span>
      </span>
      <input
        type="checkbox"
        className="mt-1 size-4 accent-[color:var(--color-ruby-red)]"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  )
}

export function CookieConsent() {
  const [locale, setLocale] = useState<Locale>('en')
  const [consent, setConsent] = useState<ConsentRecord | null>(null)
  const [ready, setReady] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES)
  const [error, setError] = useState<string | null>(null)
  const analyticsConsent = consent?.preferences.analytics === true

  useEffect(() => {
    setLocale(currentLocale())
    setConsent(readConsent())
    setReady(true)

    const updateLocale = () => setLocale(currentLocale())
    document.addEventListener('inertia:navigate', updateLocale)
    return () => document.removeEventListener('inertia:navigate', updateLocale)
  }, [])

  useEffect(() => {
    const id = measurementId()
    if (!id) return
    if (analyticsConsent) configureGoogleAnalytics(id, true)
    else if (typeof window.gtag === 'function') configureGoogleAnalytics(id, false)
  }, [analyticsConsent])

  useEffect(() => {
    if (!analyticsConsent) return

    const reportPageView = () => {
      trackPageView(`${window.location.pathname}${window.location.search}`)
    }
    const reportLinkClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return
      const link = event.target.closest<HTMLAnchorElement>('a[href]')
      if (!link || link.hasAttribute('download')) return

      const url = new URL(link.href, window.location.href)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return

      trackEvent('link_click', {
        link_url: url.toString(),
        link_text: (link.innerText || link.getAttribute('aria-label') || '').trim().slice(0, 100),
        link_domain: url.hostname,
        outbound: url.origin !== window.location.origin,
      })
    }

    reportPageView()
    document.addEventListener('inertia:navigate', reportPageView)
    document.addEventListener('click', reportLinkClick)
    return () => {
      document.removeEventListener('inertia:navigate', reportPageView)
      document.removeEventListener('click', reportLinkClick)
    }
  }, [analyticsConsent])

  useEffect(() => {
    if (!preferencesOpen) return
    setPreferences(consent?.preferences ?? DEFAULT_PREFERENCES)
  }, [consent, preferencesOpen])

  useEffect(() => {
    if (!preferencesOpen) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreferencesOpen(false)
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [preferencesOpen])

  function saveConsent(nextPreferences: ConsentPreferences) {
    try {
      const nextConsent: ConsentRecord = {
        version: 1,
        updatedAt: new Date().toISOString(),
        locale,
        preferences: nextPreferences,
      }
      writeConsent(nextConsent)
      setConsent(nextConsent)
      setError(null)
      setPreferencesOpen(false)
      if (nextPreferences.analytics) {
        trackEvent('cookie_consent', { action: 'save', analytics: true })
      }
    } catch (saveError) {
      console.error('Could not save cookie consent preferences.', saveError)
      setError(t(locale, 'cookieError'))
    }
  }

  if (!ready) return null

  return (
    <>
      {consent ? (
        <Button
          type="button"
          variant="outline"
          className="fixed bottom-4 right-4 z-50 border-ruby-navy/20 bg-white dark:bg-slate-navy text-ruby-navy shadow-md"
          onClick={() => setPreferencesOpen(true)}
        >
          {t(locale, 'cookieManage')}
        </Button>
      ) : (
        <div className="fixed inset-x-0 bottom-0 z-[60] p-4 md:p-6">
          <section aria-labelledby="cookie-banner-title" className="mx-auto max-w-4xl rounded-2xl border bg-white dark:bg-slate-navy p-5 shadow-lg md:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <h2 id="cookie-banner-title" className="text-base font-semibold text-ruby-dark">
                  {t(locale, 'cookieBannerTitle')}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-mid-blue-gray">{t(locale, 'cookieBannerBody')}</p>
                <a
                  className="mt-2 inline-flex text-sm font-medium text-ruby-red hover:underline"
                  href={`/${locale}/cookie-policy`}
                >
                  {t(locale, 'cookieBannerPolicyLink')}
                </a>
              </div>
              <div className="flex w-full flex-col gap-2 md:w-auto md:min-w-[260px]">
                {error && <p role="alert" className="text-sm font-medium text-ruby-red">{error}</p>}
                <Button type="button" onClick={() => saveConsent({ analytics: true, marketing: true, preferences: true })}>
                  {t(locale, 'cookieAcceptAll')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveConsent(DEFAULT_PREFERENCES)}
                >
                  {t(locale, 'cookieRejectAll')}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setPreferencesOpen(true)}>
                  {t(locale, 'cookieManage')}
                </Button>
              </div>
            </div>
          </section>
        </div>
      )}

      {preferencesOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-4 md:items-center"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPreferencesOpen(false)
          }}
        >
          <section
            aria-labelledby="cookie-preferences-title"
            aria-modal="true"
            className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border bg-white dark:bg-slate-navy p-6 shadow-xl md:p-8"
            role="dialog"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="cookie-preferences-title" className="text-xl font-bold text-ruby-dark">
                  {t(locale, 'cookiePreferencesTitle')}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-mid-blue-gray">{t(locale, 'cookiePreferencesBody')}</p>
                <a className="mt-2 inline-flex text-sm font-medium text-ruby-red hover:underline" href={`/${locale}/cookie-policy`}>
                  {t(locale, 'cookieBannerPolicyLink')}
                </a>
              </div>
              <Button type="button" variant="outline" onClick={() => setPreferencesOpen(false)}>
                {t(locale, 'cookieClose')}
              </Button>
            </div>

            <div className="mt-6 grid gap-3">
              <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
                <span>
                  <span className="block text-sm font-semibold text-ruby-dark">{t(locale, 'cookieNecessary')}</span>
                  <span className="mt-1 block text-sm text-mid-blue-gray">{t(locale, 'cookieNecessaryDesc')}</span>
                </span>
                <span className="whitespace-nowrap rounded-md bg-ruby-navy px-2.5 py-1 text-xs font-medium text-white dark:text-slate-navy">
                  {t(locale, 'cookieAlwaysOn')}
                </span>
              </div>
              <PreferenceToggle
                label={t(locale, 'cookieAnalytics')}
                description={t(locale, 'cookieAnalyticsDesc')}
                checked={preferences.analytics}
                onChange={(analytics) => setPreferences((current) => ({ ...current, analytics }))}
              />
              <PreferenceToggle
                label={t(locale, 'cookieMarketing')}
                description={t(locale, 'cookieMarketingDesc')}
                checked={preferences.marketing}
                onChange={(marketing) => setPreferences((current) => ({ ...current, marketing }))}
              />
              <PreferenceToggle
                label={t(locale, 'cookiePreferences')}
                description={t(locale, 'cookiePreferencesDesc')}
                checked={preferences.preferences}
                onChange={(preference) => setPreferences((current) => ({ ...current, preferences: preference }))}
              />
            </div>

            {error && <p role="alert" className="mt-4 text-sm font-medium text-ruby-red">{error}</p>}
            <div className="mt-6 flex flex-col gap-2 md:flex-row md:justify-end">
              <Button type="button" variant="outline" onClick={() => saveConsent(DEFAULT_PREFERENCES)}>
                {t(locale, 'cookieRejectAll')}
              </Button>
              <Button type="button" onClick={() => saveConsent(preferences)}>
                {t(locale, 'cookieSave')}
              </Button>
            </div>
          </section>
        </div>
      )}
    </>
  )
}
